import { useCallback, useEffect, useId, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface CompareSliderProps {
  beforeSrc: string;
  afterSrc: string;
  beforeAlt?: string;
  afterAlt?: string;
  className?: string;
}

export function CompareSlider({
  beforeSrc,
  afterSrc,
  beforeAlt = "Original",
  afterAlt = "Upscaled",
  className,
}: CompareSliderProps) {
  const [position, setPosition] = useState(46);
  const frameRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const labelId = useId();

  const moveTo = useCallback((clientX: number) => {
    const frame = frameRef.current;
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPosition(Math.min(100, Math.max(0, next)));
  }, []);

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      if (!dragging.current) return;
      moveTo(event.clientX);
    };
    const onPointerUp = () => {
      dragging.current = false;
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [moveTo]);

  return (
    <div className={cn("panel overflow-hidden", className)}>
      <div
        ref={frameRef}
        className="relative aspect-[4/3] w-full cursor-ew-resize select-none bg-ink sm:aspect-[16/10]"
        onPointerDown={(event) => {
          dragging.current = true;
          (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
          moveTo(event.clientX);
        }}
      >
        <img
          src={afterSrc}
          alt={afterAlt}
          draggable={false}
          className="absolute inset-0 h-full w-full object-contain"
        />
        <img
          src={beforeSrc}
          alt={beforeAlt}
          draggable={false}
          className="absolute inset-0 h-full w-full object-contain"
          style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
        />
        <div
          className="absolute inset-y-0 z-10 w-[3px] bg-acid"
          style={{ left: `${position}%` }}
        >
          <div className="absolute left-1/2 top-1/2 grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 place-items-center border-[3px] border-ink bg-tape font-slam text-lg text-ink shadow-smash-sm">
            ||
          </div>
        </div>
        <span className="absolute left-3 top-3 border-[3px] border-ink bg-cream px-1.5 py-0.5 font-mono text-[10px] uppercase">
          Before
        </span>
        <span className="absolute right-3 top-3 border-[3px] border-ink bg-acid px-1.5 py-0.5 font-mono text-[10px] uppercase">
          After
        </span>
      </div>
      <div className="flex items-center gap-3 border-t-[3px] border-ink bg-paper px-4 py-3">
        <label htmlFor={labelId} className="font-mono text-[10px] uppercase">
          Split
        </label>
        <input
          id={labelId}
          type="range"
          min={0}
          max={100}
          value={Math.round(position)}
          onChange={(event) => setPosition(Number(event.target.value))}
          className="w-full"
          aria-label="Comparison split"
        />
        <span className="w-10 text-right font-mono text-xs">
          {Math.round(position)}%
        </span>
      </div>
    </div>
  );
}
